// frontend/src/pages/ReviewForm.js
// Full-page performance review form. Replaces the modal flow so comment
// boxes have room to breathe.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getKPIReviewForm, submitManagerScores, submitReview, getUser, getCycles, getReviews,
} from '../services/api';
import {
  Card, Avatar, Badge, Btn, Field, Select, Textarea,
  SectionTitle, Spinner, ProgressBar,
} from '../components/UI';
import { scoreToCalibration } from '../components/UI';
import { kpiScoreSummary } from '../utils/kpiScore';

const CALIBRATIONS = [
  { value: 'exceptional',       label: 'Exceptional — 4.5 to 5.0' },
  { value: 'excellent',         label: 'Excellent — 3.8 to 4.4' },
  { value: 'good',              label: 'Good — 3.0 to 3.7' },
  { value: 'needs_improvement', label: 'Needs Improvement — 2.0 to 2.9' },
  { value: 'unsatisfactory',    label: 'Unsatisfactory — below 2.0' },
];
const WORKFLOW_LABELS = { draft:'Draft', pending_self:'Awaiting self-eval', pending_manager:'Pending manager', finalised:'Finalised' };
const WORKFLOW_COLORS = { draft:'gray', pending_self:'amber', pending_manager:'blue', finalised:'green' };

// ── 1–5 star rating control ──────────────────────────────────────────────
function StarRating({ value, onChange, label, readOnly = false }) {
  return (
    <div style={{ marginBottom: 4 }}>
      {label && <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 5 }}>{label}</div>}
      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
        {[1,2,3,4,5].map(n => (
          <button key={n} type="button"
            onClick={() => !readOnly && onChange && onChange(n)}
            style={{
              width: 32, height: 32, border: 'none', borderRadius: 6, cursor: readOnly ? 'default' : 'pointer',
              background: n <= (value || 0) ? 'var(--navy-500)' : 'var(--gray-100)',
              color: n <= (value || 0) ? '#fff' : 'var(--text-3)',
              fontSize: 15, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'inherit', transition: 'all 0.1s',
            }}>
            {n}
          </button>
        ))}
        {value > 0 && <span style={{ fontSize: 13, color: 'var(--text-2)', marginLeft: 8 }}>
          {['','Below expectations','Needs improvement','Meets expectations','Exceeds expectations','Outstanding'][value]}
        </span>}
      </div>
    </div>
  );
}

// ── One KPI row (self-eval context + manager scoring) ────────────────────
function KPIRow({ kpi, managerScore, managerComment, onScoreChange, onCommentChange, isManager }) {
  const selfScore = kpi.self_score;
  return (
    <div style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '16px 18px', marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 500 }}>{kpi.title}</div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 3 }}>
            Weight: <strong>{kpi.weight}%</strong>
            {kpi.category && <> · {kpi.category}</>}
            {kpi.target && <> · Target: {kpi.target}</>}
          </div>
        </div>
        <Badge color={WORKFLOW_COLORS[kpi.workflow_status] || 'gray'}>
          {WORKFLOW_LABELS[kpi.workflow_status] || kpi.workflow_status}
        </Badge>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Self-eval */}
        <div>
          <StarRating value={selfScore} readOnly label="Employee self-rating" />
          {kpi.self_comment ? (
            <div style={{ fontSize: 12, color: 'var(--text-2)', fontStyle: 'italic', marginTop: 8, padding: '10px 12px', background: 'var(--gray-50)', borderRadius: 6, borderLeft: '3px solid var(--navy-500)', lineHeight: 1.5 }}>
              "{kpi.self_comment}"
            </div>
          ) : selfScore ? (
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6, fontStyle: 'italic' }}>
              No self-eval comment provided.
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--amber-700)', marginTop: 6 }}>Awaiting self-evaluation</div>
          )}
        </div>

        {/* Manager scoring */}
        <div>
          {isManager ? (
            <>
              <StarRating value={managerScore} onChange={onScoreChange} label="Manager rating" />
              <textarea value={managerComment} onChange={e => onCommentChange(e.target.value)}
                placeholder="Your feedback, evidence, and rationale for this score. Be specific — what went well, what needs improvement, what behaviour you want to reinforce or change."
                rows={5}
                style={{
                  width: '100%', fontSize: 13, padding: '10px 12px', marginTop: 10,
                  border: '0.5px solid var(--border-md)', borderRadius: 'var(--radius-md)',
                  resize: 'vertical', fontFamily: 'inherit',
                  color: 'var(--text-1)', background: 'var(--surface)',
                  minHeight: 110, lineHeight: 1.5,
                }} />
            </>
          ) : (
            <>
              <StarRating value={kpi.manager_score} readOnly label="Manager rating" />
              {kpi.manager_comment && (
                <div style={{ fontSize: 12, color: 'var(--text-2)', fontStyle: 'italic', marginTop: 8, padding: '10px 12px', background: 'var(--gray-50)', borderRadius: 6, borderLeft: '3px solid var(--navy-600)', lineHeight: 1.5 }}>
                  "{kpi.manager_comment}"
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {(kpi.manager_score || managerScore) > 0 && (
        <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-3)', borderTop: '0.5px solid var(--border)', paddingTop: 10 }}>
          Weighted contribution: <strong style={{ color: 'var(--navy-600)' }}>
            {(((Number(kpi.weight) || 0) / 100) * (managerScore || kpi.manager_score || 0)).toFixed(2)}
          </strong> pts ({kpi.weight}% × {managerScore || kpi.manager_score || 0})
        </div>
      )}
    </div>
  );
}

export default function ReviewForm() {
  const { user }     = useAuth();
  const navigate     = useNavigate();
  const { userId }   = useParams();
  const [searchParams] = useSearchParams();
  const cycleId      = searchParams.get('cycle') || '';
  const isManagerRole = ['admin','manager','dept_head'].includes(user?.role);

  const [reviewee,    setReviewee]    = useState(null);
  const [cycle,       setCycle]       = useState(null);
  const [formData,    setFormData]    = useState(null);
  const [kpiScores,   setKpiScores]   = useState({});
  const [summaryForm, setSummaryForm] = useState({ calibration: 'good', summary_notes: '' });
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);

  useEffect(() => {
    if (!userId || !cycleId) { setLoading(false); return; }
    Promise.all([
      getUser(userId).catch(() => null),
      getCycles().catch(() => []),
      getKPIReviewForm(userId, cycleId).catch(() => ({ kpis: [], okrs: [], weight_total: 0 })),
      getReviews({ cycle_id: cycleId, user_id: userId }).catch(() => []),
    ]).then(([u, cycles, form, reviews]) => {
      setReviewee(u);
      setCycle((Array.isArray(cycles) ? cycles : []).find(c => String(c.id) === String(cycleId)) || null);
      setFormData(form);
      // Pre-fill existing manager scores
      const existing = {};
      (form.kpis || []).forEach(k => {
        if (k.manager_score) existing[k.id] = { score: Number(k.manager_score), comment: k.manager_comment || '' };
      });
      setKpiScores(existing);
      // Pre-fill existing summary (calibration + narrative) from the manager review
      const mine = (Array.isArray(reviews) ? reviews : []).find(
        r => r.review_type === 'manager' && Number(r.reviewer_id) === Number(user?.id)
      );
      if (mine) {
        setSummaryForm({
          calibration: CALIBRATIONS.some(c => c.value === mine.calibration) ? mine.calibration : 'good',
          summary_notes: mine.summary_notes || '',
        });
      }
    }).finally(() => setLoading(false));
  }, [userId, cycleId, user?.id]);

  const cycleClosed = cycle?.status === 'closed' && Number(cycle?.program_id) === 1;
  const isManager   = isManagerRole && !cycleClosed;

  const liveFinalScore = useMemo(() => {
    return (formData?.kpis || []).reduce((sum, k) => {
      const s = kpiScores[k.id]?.score || k.manager_score || 0;
      return sum + ((Number(k.weight) || 0) / 100) * s;
    }, 0);
  }, [formData, kpiScores]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!isManager) return;

    // Compute the real final score; null unless every KPI is scored. Never fabricate
    // a value (the old `finalScore || 3` wrote a bogus 3/5 for unscored reviews).
    const kpis = formData?.kpis || [];
    const summary = kpiScoreSummary(kpis, k => {
      const s = kpiScores[k.id]?.score || k.manager_score;
      return s > 0 ? Number(s) : null;
    });
    if (kpis.length > 0 && !summary.allScored) {
      alert('Please score every KPI before submitting — a review cannot be finalised with unscored KPIs.');
      return;
    }

    setSaving(true);
    try {
      const scoredKpis = Object.entries(kpiScores)
        .filter(([, v]) => v.score > 0)
        .map(([id, v]) => ({
          assignment_id: parseInt(id),
          score: v.score,
          manager_comment: v.comment,
          finalise: true,
        }));
      if (scoredKpis.length > 0) await submitManagerScores(scoredKpis);

      const finalScore = summary.finalScore; // number when all scored; null when there are no KPIs
      await submitReview({
        cycle_id: cycleId,
        reviewee_id: userId,
        review_type: 'manager',
        delivery_score: finalScore,
        collab_score:   finalScore,
        impact_score:   finalScore,
        overall_score:  finalScore,
        calibration:    finalScore != null ? scoreToCalibration(finalScore) : summaryForm.calibration,
        summary_notes:  summaryForm.summary_notes,
        status:         'submitted',
      });
      navigate('/reviews');
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving review');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner />;
  if (!userId || !cycleId) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 14, color: 'var(--text-3)' }}>Missing employee or cycle.</div>
        <Btn variant="secondary" size="sm" onClick={() => navigate('/reviews')} style={{ marginTop: 12 }}>Back to reviews</Btn>
      </div>
    );
  }

  const kpis       = formData?.kpis || [];
  const okrs       = formData?.okrs || [];
  const weightTotal = formData?.weight_total || 0;

  return (
    <div style={{ padding: '20px clamp(16px, 3vw, 36px)', maxWidth: 1080, margin: '0 auto' }}>
      {/* Breadcrumb */}
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 12 }}>
        <Link to="/reviews" style={{ color: 'var(--navy-600)', textDecoration: 'none' }}>Performance reviews</Link>
        {' · '}Review form
      </div>

      {/* Header card */}
      <Card style={{ marginBottom: 18, padding: '18px 22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Avatar initials={reviewee?.avatar_initials} size={48} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 500 }}>{reviewee?.name || 'Employee'}</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 3 }}>
              {reviewee?.job_title || '—'}
              {reviewee?.department_name && <> · {reviewee.department_name}</>}
              {cycle && <> · Cycle: <strong style={{ color: 'var(--text-2)' }}>{cycle.name}</strong></>}
            </div>
          </div>
          <Btn type="button" variant="secondary" size="sm" onClick={() => navigate('/reviews')}>← Back</Btn>
        </div>
      </Card>

      {cycleClosed && (
        <Card style={{ marginBottom: 16, background: 'var(--gray-50)', borderLeft: '3px solid var(--text-3)' }}>
          <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
            This cycle is closed — reviews and KPI ratings are read-only.
          </div>
        </Card>
      )}

      <form onSubmit={handleSubmit}>
        {/* OKR context */}
        {okrs.length > 0 && (
          <Card style={{ marginBottom: 16 }}>
            <SectionTitle>OKR context this cycle</SectionTitle>
            <div style={{ marginTop: 10 }}>
              {okrs.map((o, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <ProgressBar value={o.progress} height={6} color={o.status === 'at_risk' ? 'var(--amber-500)' : 'var(--navy-500)'} />
                  <span style={{ fontSize: 12, color: 'var(--text-3)', width: 36, textAlign: 'right' }}>{o.progress}%</span>
                  <span style={{ fontSize: 12, color: 'var(--text-2)', flex: 1 }}>{o.title}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* KPI scoring */}
        {kpis.length > 0 ? (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <SectionTitle>KPI scores (1–5)</SectionTitle>
              <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
                Weights total: <strong style={{ color: weightTotal === 100 ? 'var(--navy-600)' : 'var(--red-700)' }}>{weightTotal}%</strong>
              </div>
            </div>
            {kpis.map(kpi => (
              <KPIRow key={kpi.id} kpi={kpi}
                managerScore={kpiScores[kpi.id]?.score || 0}
                managerComment={kpiScores[kpi.id]?.comment || ''}
                onScoreChange={v => setKpiScores(s => ({ ...s, [kpi.id]: { ...s[kpi.id], score: v } }))}
                onCommentChange={v => setKpiScores(s => ({ ...s, [kpi.id]: { ...s[kpi.id], comment: v } }))}
                isManager={isManager}
              />
            ))}

            {liveFinalScore > 0 && (
              <div style={{ background: 'var(--navy-50)', borderRadius: 10, padding: '14px 18px', marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: 'var(--navy-600)' }}>Final weighted score (Σ weight × score)</span>
                <span style={{ fontSize: 28, fontWeight: 500, color: 'var(--navy-600)' }}>
                  {liveFinalScore.toFixed(2)}<span style={{ fontSize: 15, color: 'var(--text-3)' }}>/5</span>
                </span>
              </div>
            )}
          </Card>
        ) : (
          <Card style={{ marginBottom: 16, background: 'var(--amber-50)' }}>
            <div style={{ fontSize: 13, color: 'var(--amber-700)' }}>
              No KPIs assigned for this employee in this cycle. Assign KPIs first in the KPIs & Bonus page.
            </div>
          </Card>
        )}

        {/* Summary */}
        <Card style={{ marginBottom: 20 }}>
          <SectionTitle>Summary & development notes</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 14, marginTop: 10 }}>
            <Field label="Calibration rating">
              <Select value={summaryForm.calibration} onChange={e => setSummaryForm(f => ({ ...f, calibration: e.target.value }))} disabled={cycleClosed}>
                {CALIBRATIONS.map(cal => <option key={cal.value} value={cal.value}>{cal.label}</option>)}
              </Select>
            </Field>
            <Field label="Overall feedback, strengths, and areas for development">
              <Textarea
                value={summaryForm.summary_notes}
                onChange={e => setSummaryForm(f => ({ ...f, summary_notes: e.target.value }))}
                placeholder="Use this space for narrative feedback — highlight what's going well, specific examples of impact, and clear development actions for the next cycle. Anything you write here informs the development plan."
                rows={10}
                readOnly={cycleClosed}
                style={{ minHeight: 200, fontSize: 13, lineHeight: 1.55, padding: '12px 14px' }}
              />
            </Field>
          </div>
        </Card>

        {/* Sticky action bar */}
        <div style={{
          position: 'sticky', bottom: 0, background: 'var(--surface)',
          borderTop: '0.5px solid var(--border-md)', padding: '14px 2px',
          display: 'flex', justifyContent: 'flex-end', gap: 10, zIndex: 10,
        }}>
          <Btn type="button" variant="secondary" onClick={() => navigate('/reviews')}>{cycleClosed ? 'Back' : 'Cancel'}</Btn>
          {!cycleClosed && (
            <Btn type="submit" disabled={saving || !isManager}>
              {saving ? 'Saving…' : 'Submit review & finalise KPIs'}
            </Btn>
          )}
        </div>
      </form>
    </div>
  );
}
